import { useMutation } from "@tanstack/react-query";
import { use } from "react";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { useTranslator } from "../i18n/translator.ts";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import {
  useCancelCardListQueries,
  useToggleCardSignatureCache,
} from "./useToggleCardSignatureCache.ts";

export const useToggleCardSignature = ({
  cardId,
  projectId,
  onChanged,
}: {
  cardId: string | undefined;
  projectId: string;
  onChanged?: (newState: boolean) => void;
}) => {
  const translator = useTranslator();
  const { getToken } = useAuthentication();
  const { displayError, displaySuccess } = use(SnackbarContext);
  const toggleCardSignatureCache = useToggleCardSignatureCache();
  const cancelCardListQueries = useCancelCardListQueries();

  return useMutation({
    mutationKey: ["cards", cardId, "signatures", "own"],
    onMutate: async () => {
      await cancelCardListQueries(projectId);
    },
    mutationFn: async (isCurrentlySigned: boolean) => {
      const token = await getToken();
      const response = await fetch(
        `/api/projects/${projectId}/cards/${cardId}/signatures`,
        {
          method: isCurrentlySigned ? "DELETE" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Changing the signature failed");
      }
      onChanged?.(!isCurrentlySigned);
    },
    onSuccess: (_, isCurrentlySigned: boolean) => {
      toggleCardSignatureCache(cardId, projectId, !isCurrentlySigned);
      displaySuccess(translator("changesSaved"));
    },
    onError: () => {
      displayError(translator("errorSavingChanges"));
    },
  });
};
